import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Megaphone, Plus, Send, Archive, Loader2, Eye, CheckCircle2, Pencil, BarChart3, ImagePlus, X, ArrowLeft, ArrowRight, Star, Images, Users, Paperclip, Headphones, FileText } from 'lucide-react';
import { commImgSrc, COMM_CATEGORY_META, CommImageCarousel } from '@/components/shared/CommImageCarousel';

const EMPTY = { title: '', body: '', priority: 'normal', category: 'general', event_date: '', location: '',
  publish_at: '', expires_at: '', images: [], attachments: [],
  attendance_enabled: false, attendance_capacity: null, attendance_deadline: '',
  require_read_confirmation: false, audience: { type: 'company', department_ids: [], user_ids: [] } };

const STATUS_CLS = {
  draft: 'bg-slate-100 text-slate-600 border-slate-300',
  scheduled: 'bg-blue-100 text-blue-700 border-blue-300',
  published: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  archived: 'bg-zinc-100 text-zinc-500 border-zinc-300',
};

export default function ClientCommunications() {
  const { i18n } = useTranslation();
  const isES = !(i18n.language || '').startsWith('en');
  const L = (es, en) => (isES ? es : en);
  const [rows, setRows] = useState(null);
  const [depts, setDepts] = useState([]);
  const [people, setPeople] = useState([]);
  const [draft, setDraft] = useState(null); // null | {id?, ...EMPTY}
  const [stats, setStats] = useState(null);
  const [attend, setAttend] = useState(null); // {comm, data}
  const [remBusy, setRemBusy] = useState(false);

  const openAttendees = async (c) => {
    try {
      const { data } = await api.get(`/client/communications/${c.id}/attendees`);
      setAttend({ comm: c, data });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const sendReminder = async (audience) => {
    setRemBusy(true);
    try {
      const { data } = await api.post(`/client/communications/${attend.comm.id}/reminders`, { audience });
      toast.success(L(`Recordatorio registrado (${data.target_count} destinatarios, solo en la app)`,
                      `Reminder recorded (${data.target_count} recipients, in-app only)`));
    } catch (e) { toast.error(formatApiError(e)); } finally { setRemBusy(false); }
  };
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [peopleQ, setPeopleQ] = useState('');
  const fileRef = React.useRef(null);

  const load = () => api.get('/client/communications').then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    load();
    api.get('/client/departments').then((r) => setDepts(r.data || [])).catch(() => {});
    api.get('/client/users').then((r) => setPeople((r.data || []).filter(
      (u) => u.employment_status !== 'former' && !['inactive', 'disabled'].includes(u.status)))).catch(() => {});
  }, []);

  const STATUS_LBL = {
    draft: L('Borrador', 'Draft'), scheduled: L('Programada', 'Scheduled'),
    published: L('Publicada', 'Published'), archived: L('Archivada', 'Archived'),
  };

  const filteredPeople = useMemo(() => people.filter((u) =>
    !peopleQ.trim() || (u.name || '').toLowerCase().includes(peopleQ.toLowerCase())
    || (u.email || '').toLowerCase().includes(peopleQ.toLowerCase())), [people, peopleQ]);

  const save = async () => {
    if (draft.title.trim().length < 2 || draft.body.trim().length < 2) {
      toast.error(L('Título y contenido son obligatorios.', 'Title and content are required.')); return;
    }
    setBusy(true);
    const payload = { ...draft,
      event_date: draft.event_date || null,
      location: draft.location || null,
      attendance_deadline: draft.attendance_deadline || null,
      images: (draft.images || []).map(({ file_id, url, alt }) => ({ file_id, url, alt: alt || null })),
      attachments: (draft.attachments || []).map(({ file_id, url, kind, name, description, size, content_type }) =>
        ({ file_id, url, kind, name, description: description || null, size: size || 0, content_type: content_type || '' })),
      publish_at: draft.publish_at ? new Date(draft.publish_at).toISOString() : null,
      expires_at: draft.expires_at ? new Date(draft.expires_at).toISOString() : null };
    delete payload.id;
    try {
      if (draft.id) await api.patch(`/client/communications/${draft.id}`, payload);
      else await api.post('/client/communications', payload);
      toast.success(L('Comunicación guardada', 'Communication saved'));
      setDraft(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/client/communications/${id}/${action}`);
      toast.success(action === 'publish' ? L('Publicada', 'Published') : L('Archivada', 'Archived'));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openStats = async (c) => {
    try { const { data } = await api.get(`/client/communications/${c.id}/stats`); setStats(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : '');

  // CTH-COM-075 — multi-image upload / reorder / cover / alt text
  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    if ((draft.images || []).length + files.length > 10) {
      toast.error(L('Máximo 10 imágenes por comunicación.', 'Maximum 10 images per communication.')); return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) { toast.error(L(`${f.name} supera 5 MB.`, `${f.name} exceeds 5 MB.`)); continue; }
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await api.post('/client/communications/images', fd,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
        setDraft((d) => ({ ...d, images: [...(d.images || []), { file_id: data.file_id, url: data.url, alt: '' }] }));
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
  };
  const moveImg = (i, dir) => setDraft((d) => {
    const imgs = [...(d.images || [])];
    const j = i + dir;
    if (j < 0 || j >= imgs.length) return d;
    [imgs[i], imgs[j]] = [imgs[j], imgs[i]];
    return { ...d, images: imgs };
  });
  const makeCover = (i) => setDraft((d) => {
    const imgs = [...(d.images || [])];
    const [img] = imgs.splice(i, 1);
    return { ...d, images: [img, ...imgs] };
  });
  const removeImg = (i) => setDraft((d) => ({ ...d, images: (d.images || []).filter((_, x) => x !== i) }));
  const setAlt = (i, alt) => setDraft((d) => ({ ...d, images: (d.images || []).map((img, x) => x === i ? { ...img, alt } : img) }));

  // CTH-COM-087 — audio (MP3/M4A ≤20MB) + PDF (≤25MB) attachments
  const audioRef = React.useRef(null);
  const pdfRef = React.useRef(null);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const addAttachment = async (fileList, expectedKind) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if ((draft.attachments || []).length + files.length > 5) {
      toast.error(L('Máximo 5 adjuntos por comunicación.', 'Maximum 5 attachments per communication.')); return;
    }
    setUploadingAtt(true);
    try {
      for (const f of files) {
        const maxMB = expectedKind === 'pdf' ? 25 : 20;
        if (f.size > maxMB * 1024 * 1024) {
          toast.error(L(`"${f.name}" supera el máximo de ${maxMB} MB.`, `"${f.name}" exceeds the ${maxMB} MB limit.`));
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await api.post('/client/communications/attachments', fd,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
        setDraft((d) => ({ ...d, attachments: [...(d.attachments || []),
          { file_id: data.file_id, url: data.url, kind: data.kind, name: data.name, description: '', size: data.size, content_type: data.content_type }] }));
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setUploadingAtt(false); }
  };
  const removeAtt = (i) => setDraft((d) => ({ ...d, attachments: (d.attachments || []).filter((_, x) => x !== i) }));
  const setAttDesc = (i, description) => setDraft((d) => ({ ...d, attachments: (d.attachments || []).map((a, x) => x === i ? { ...a, description } : a) }));
  const setAttName = (i, name) => setDraft((d) => ({ ...d, attachments: (d.attachments || []).map((a, x) => x === i ? { ...a, name } : a) }));

  return (
    <div className="min-h-screen bg-background">
      <Topbar title={L('Comunicaciones', 'Communications')} />
      <div className="p-6 max-w-6xl space-y-4" data-testid="client-communications-page">
        <Card className="p-4 shadow-card flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-teal-700" />
            <div>
              <p className="font-semibold">{L('Comunicaciones corporativas', 'Corporate communications')}</p>
              <p className="text-xs text-muted-foreground">
                {L('Crea, programa y publica comunicados con confirmación de lectura opcional.', 'Create, schedule and publish announcements with optional read confirmation.')}
              </p>
            </div>
          </div>
          <Button onClick={() => setDraft({ ...EMPTY })} data-testid="comm-create-btn">
            <Plus className="h-4 w-4 mr-1.5" />{L('Nueva comunicación', 'New communication')}
          </Button>
        </Card>

        {rows === null ? (
          <Card className="p-10 text-center text-sm text-muted-foreground">…</Card>
        ) : rows.length === 0 ? (
          <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="comm-empty">
            {L('Aún no hay comunicaciones. Crea la primera para tu equipo.', 'No communications yet. Create the first one for your team.')}
          </Card>
        ) : (
          <Card className="shadow-card overflow-x-auto" data-testid="comm-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{L('Título', 'Title')}</TableHead>
                  <TableHead>{L('Estado', 'Status')}</TableHead>
                  <TableHead>{L('Prioridad', 'Priority')}</TableHead>
                  <TableHead>{L('Destinatarios', 'Recipients')}</TableHead>
                  <TableHead>{L('Vistas', 'Views')}</TableHead>
                  <TableHead>{L('Confirmaciones', 'Confirmations')}</TableHead>
                  <TableHead className="text-right">{L('Acciones', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.id} data-testid={`comm-row-${c.id}`}>
                    <TableCell className="font-medium max-w-64">
                      <span className="line-clamp-2">{c.title}</span>
                      {(c.images || []).length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5" data-testid={`comm-row-images-${c.id}`}>
                          <Images className="h-3 w-3" />{c.images.length} {L('imágenes', 'images')}
                        </span>
                      )}
                      {(c.attachments || []).length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5 ml-2" data-testid={`comm-row-attachments-${c.id}`}>
                          <Paperclip className="h-3 w-3" />{c.attachments.length} {L('adjunto(s)', 'attachment(s)')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${STATUS_CLS[c.status]}`}>{STATUS_LBL[c.status]}</Badge></TableCell>
                    <TableCell className="text-xs capitalize">{c.priority === 'urgent' ? L('Urgente','Urgent') : c.priority === 'high' ? L('Alta','High') : L('Normal','Normal')}</TableCell>
                    <TableCell className="tabular-nums text-sm">{c.recipient_count}</TableCell>
                    <TableCell className="tabular-nums text-sm">{c.view_count}</TableCell>
                    <TableCell className="tabular-nums text-sm">{c.require_read_confirmation ? c.confirm_count : '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        {c.status !== 'archived' && (
                          <Button size="sm" variant="ghost" className="h-7 px-2"
                                  onClick={() => setDraft({ ...EMPTY, ...c, images: c.images || [], attachments: c.attachments || [], category: c.category || 'general',
                                    event_date: c.event_date || '', location: c.location || '',
                                    attendance_enabled: !!c.attendance_enabled,
                                    attendance_capacity: c.attendance_capacity || null,
                                    attendance_deadline: c.attendance_deadline || '',
                                    publish_at: toLocalInput(c.publish_at), expires_at: toLocalInput(c.expires_at) })}
                                  data-testid={`comm-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        )}
                        {(c.status === 'draft' || c.status === 'scheduled') && (
                          <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => act(c.id, 'publish')} data-testid={`comm-publish-${c.id}`}>
                            <Send className="h-3.5 w-3.5 mr-1" />{L('Publicar', 'Publish')}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openStats(c)} data-testid={`comm-stats-${c.id}`}>
                          <BarChart3 className="h-3.5 w-3.5" />
                        </Button>
                        {c.attendance_enabled && (
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openAttendees(c)}
                                  data-testid={`comm-attendees-${c.id}`}>
                            <Users className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {c.status !== 'archived' && (
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => act(c.id, 'archive')} data-testid={`comm-archive-${c.id}`}>
                            <Archive className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="comm-dialog">
          <DialogHeader>
            <DialogTitle>{draft?.id ? L('Editar comunicación', 'Edit communication') : L('Nueva comunicación', 'New communication')}</DialogTitle>
          </DialogHeader>
          {draft && (
            <div className="space-y-4">
              <Input value={draft.title} maxLength={200} placeholder={L('Título', 'Title')}
                     onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="comm-title" />
              <Textarea value={draft.body} maxLength={8000} rows={6} placeholder={L('Contenido del comunicado…', 'Announcement content…')}
                        onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="comm-body" />

              {/* CTH-COM-075 — promotional image carousel management */}
              <div className="rounded-lg border border-dashed p-3 space-y-3"
                   onDragOver={(e) => e.preventDefault()}
                   onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                   data-testid="comm-images-zone">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs font-medium flex items-center gap-1.5">
                    <Images className="h-3.5 w-3.5 text-teal-700" />
                    {L('Imágenes promocionales (opcional)', 'Promotional images (optional)')}
                    <span className="text-muted-foreground font-normal">
                      · {L('la primera es la portada', 'the first one is the cover')} · {(draft.images || []).length}/10
                    </span>
                  </p>
                  <Button type="button" size="sm" variant="outline" disabled={uploading}
                          onClick={() => fileRef.current?.click()} data-testid="comm-images-add-btn">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ImagePlus className="h-3.5 w-3.5 mr-1.5" />}
                    {L('Agregar imágenes', 'Add images')}
                  </Button>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
                         onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                         data-testid="comm-images-input" />
                </div>
                <p className="text-[11px] text-muted-foreground" data-testid="comm-image-guidance">
                  {L('Recomendado: 1600×900 px (relación 16:9) · JPG, PNG o WebP · máx. 5 MB c/u · hasta 10 imágenes. La imagen se muestra completa, sin recorte, tal como la verán los colaboradores.',
                     'Recommended: 1600×900 px (16:9 ratio) · JPG, PNG or WebP · max 5 MB each · up to 10 images. The image is shown in full, uncropped, exactly as employees will see it.')}
                </p>
                {(draft.images || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">
                    {L('Arrastra imágenes aquí o usa “Agregar imágenes” (JPG, PNG o WebP · máx. 5 MB c/u).',
                       'Drag images here or use “Add images” (JPG, PNG or WebP · max 5 MB each).')}
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {draft.images.map((img, i) => (
                      <div key={img.file_id} className="rounded-md border overflow-hidden bg-muted/20" data-testid={`comm-image-item-${i}`}>
                        <div className="relative">
                          <img src={commImgSrc(img.url)} alt={img.alt || ''} className="w-full aspect-video object-cover" />
                          {i === 0 && (
                            <Badge className="absolute top-1.5 left-1.5 bg-teal-600 text-white border-0 text-[10px]" data-testid={`comm-image-cover-${i}`}>
                              <Star className="h-2.5 w-2.5 mr-0.5" />{L('Portada', 'Cover')}
                            </Badge>
                          )}
                          <button type="button" onClick={() => removeImg(i)}
                                  className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/55 text-white flex items-center justify-center hover:bg-rose-600"
                                  data-testid={`comm-image-remove-${i}`}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="p-1.5 space-y-1.5">
                          <Input value={img.alt || ''} maxLength={200} className="h-7 text-xs"
                                 placeholder={L('Descripción accesible (opcional)', 'Accessible description (optional)')}
                                 onChange={(e) => setAlt(i, e.target.value)} data-testid={`comm-image-alt-${i}`} />
                          <div className="flex items-center gap-1">
                            <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5" disabled={i === 0}
                                    onClick={() => moveImg(i, -1)} data-testid={`comm-image-left-${i}`}>
                              <ArrowLeft className="h-3 w-3" />
                            </Button>
                            <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5" disabled={i === draft.images.length - 1}
                                    onClick={() => moveImg(i, 1)} data-testid={`comm-image-right-${i}`}>
                              <ArrowRight className="h-3 w-3" />
                            </Button>
                            {i !== 0 && (
                              <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-xs ml-auto"
                                      onClick={() => makeCover(i)} data-testid={`comm-image-makecover-${i}`}>
                                <Star className="h-3 w-3 mr-0.5" />{L('Portada', 'Cover')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* CTH-COM-087 — audio + PDF supporting materials */}
              <div className="rounded-lg border border-dashed p-3 space-y-3" data-testid="comm-attachments-zone">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs font-medium flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5 text-teal-700" />
                    {L('Audio y documentos PDF (opcional)', 'Audio and PDF documents (optional)')}
                    <span className="text-muted-foreground font-normal">· {(draft.attachments || []).length}/5</span>
                  </p>
                  <div className="flex items-center gap-1.5">
                    <Button type="button" size="sm" variant="outline" disabled={uploadingAtt}
                            onClick={() => audioRef.current?.click()} data-testid="comm-audio-add-btn">
                      {uploadingAtt ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Headphones className="h-3.5 w-3.5 mr-1.5" />}
                      {L('Agregar audio', 'Add audio')}
                    </Button>
                    <Button type="button" size="sm" variant="outline" disabled={uploadingAtt}
                            onClick={() => pdfRef.current?.click()} data-testid="comm-pdf-add-btn">
                      {uploadingAtt ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <FileText className="h-3.5 w-3.5 mr-1.5" />}
                      {L('Agregar PDF', 'Add PDF')}
                    </Button>
                  </div>
                  <input ref={audioRef} type="file" accept=".mp3,.m4a,audio/mpeg,audio/mp4,audio/x-m4a" hidden
                         onChange={(e) => { addAttachment(e.target.files, 'audio'); e.target.value = ''; }}
                         data-testid="comm-audio-input" />
                  <input ref={pdfRef} type="file" accept=".pdf,application/pdf" hidden
                         onChange={(e) => { addAttachment(e.target.files, 'pdf'); e.target.value = ''; }}
                         data-testid="comm-pdf-input" />
                </div>
                <p className="text-[11px] text-muted-foreground" data-testid="comm-attachment-guidance">
                  {L('Audio: MP3 o M4A · máx. 20 MB c/u. Documentos: PDF · máx. 25 MB c/u. Los colaboradores podrán escuchar el audio en su portal y abrir o descargar el PDF de forma segura.',
                     'Audio: MP3 or M4A · max 20 MB each. Documents: PDF · max 25 MB each. Employees can play the audio in their portal and open or download the PDF securely.')}
                </p>
                {(draft.attachments || []).length > 0 && (
                  <div className="space-y-2">
                    {draft.attachments.map((a, i) => (
                      <div key={a.file_id} className="rounded-md border bg-muted/20 p-2.5" data-testid={`comm-attachment-item-${i}`}>
                        <div className="flex items-center gap-2">
                          {a.kind === 'audio'
                            ? <Headphones className="h-4 w-4 text-teal-700 shrink-0" />
                            : <FileText className="h-4 w-4 text-rose-600 shrink-0" />}
                          <Input value={a.name || ''} maxLength={200} className="h-7 text-xs flex-1"
                                 onChange={(e) => setAttName(i, e.target.value)} data-testid={`comm-attachment-name-${i}`} />
                          <span className="text-[10px] text-muted-foreground shrink-0">
                            {a.size ? (a.size >= 1024 * 1024 ? `${(a.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(a.size / 1024))} KB`) : ''}
                          </span>
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-rose-600"
                                  onClick={() => removeAtt(i)} data-testid={`comm-attachment-remove-${i}`}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {a.kind === 'audio' && (
                          <audio controls preload="metadata" className="w-full h-8 mt-2"
                                 src={`${process.env.REACT_APP_BACKEND_URL}${a.url}`}
                                 data-testid={`comm-attachment-preview-${i}`} />
                        )}
                        <Input value={a.description || ''} maxLength={300} className="h-7 text-xs mt-2"
                               placeholder={L('Descripción breve (opcional)', 'Short description (optional)')}
                               onChange={(e) => setAttDesc(i, e.target.value)} data-testid={`comm-attachment-desc-${i}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <p className="text-xs font-medium mb-1">{L('Categoría', 'Category')}</p>
                  <Select value={draft.category || 'general'} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                    <SelectTrigger data-testid="comm-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(COMM_CATEGORY_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{isES ? m.es : m.en}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-xs font-medium mb-1">{L('Fecha del evento (opcional)', 'Event date (optional)')}</p>
                  <Input type="date" value={draft.event_date || ''}
                         onChange={(e) => setDraft({ ...draft, event_date: e.target.value })} data-testid="comm-event-date" />
                </div>
                <div>
                  <p className="text-xs font-medium mb-1">{L('Ubicación (opcional)', 'Location (optional)')}</p>
                  <Input value={draft.location || ''} maxLength={200} placeholder={L('Ej. Oficinas Corporativas', 'E.g. Head Office')}
                         onChange={(e) => setDraft({ ...draft, location: e.target.value })} data-testid="comm-location" />
                </div>
              </div>

              {/* V5.0 §12 — event attendance (distinct from read confirmation) */}
              {draft.category === 'evento' && (
                <div className="rounded-lg border p-3 space-y-2" data-testid="comm-attendance-config">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input type="checkbox" checked={!!draft.attendance_enabled}
                           onChange={(e) => setDraft({ ...draft, attendance_enabled: e.target.checked })}
                           data-testid="comm-attendance-toggle" />
                    {L('Permitir confirmación de asistencia', 'Allow attendance confirmation')}
                  </label>
                  {draft.attendance_enabled && (
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium mb-1">{L('Cupo máximo (opcional)', 'Capacity (optional)')}</p>
                        <Input type="number" min={1} value={draft.attendance_capacity || ''}
                               onChange={(e) => setDraft({ ...draft, attendance_capacity: e.target.value ? Number(e.target.value) : null })}
                               data-testid="comm-attendance-capacity" />
                      </div>
                      <div>
                        <p className="text-xs font-medium mb-1">{L('Fecha límite de registro', 'Registration deadline')}</p>
                        <Input type="date" value={draft.attendance_deadline || ''}
                               onChange={(e) => setDraft({ ...draft, attendance_deadline: e.target.value })}
                               data-testid="comm-attendance-deadline" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* V5.0 §12 — admin carousel preview before publication */}
              {(draft.images || []).length > 0 && (
                <div data-testid="comm-admin-preview">
                  <p className="text-xs font-medium mb-1.5">{L('Vista previa del carrusel', 'Carousel preview')}</p>
                  <CommImageCarousel images={draft.images} title={draft.title} />
                </div>
              )}
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <p className="text-xs font-medium mb-1">{L('Prioridad', 'Priority')}</p>
                  <Select value={draft.priority} onValueChange={(v) => setDraft({ ...draft, priority: v })}>
                    <SelectTrigger data-testid="comm-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">{L('Normal', 'Normal')}</SelectItem>
                      <SelectItem value="high">{L('Alta', 'High')}</SelectItem>
                      <SelectItem value="urgent">{L('Urgente', 'Urgent')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-xs font-medium mb-1">{L('Publicar el (opcional)', 'Publish at (optional)')}</p>
                  <Input type="datetime-local" value={draft.publish_at}
                         onChange={(e) => setDraft({ ...draft, publish_at: e.target.value })} data-testid="comm-publish-at" />
                </div>
                <div>
                  <p className="text-xs font-medium mb-1">{L('Vence el (opcional)', 'Expires at (optional)')}</p>
                  <Input type="datetime-local" value={draft.expires_at}
                         onChange={(e) => setDraft({ ...draft, expires_at: e.target.value })} data-testid="comm-expires-at" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={draft.require_read_confirmation}
                          onCheckedChange={(v) => setDraft({ ...draft, require_read_confirmation: !!v })}
                          data-testid="comm-require-confirm" />
                {L('Requerir confirmación de lectura', 'Require read confirmation')}
              </label>
              <div>
                <p className="text-xs font-medium mb-1">{L('Destinatarios', 'Recipients')}</p>
                <Select value={draft.audience.type}
                        onValueChange={(v) => setDraft({ ...draft, audience: { type: v, department_ids: [], user_ids: [] } })}>
                  <SelectTrigger data-testid="comm-audience-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">{L('Toda la empresa', 'Whole company')}</SelectItem>
                    <SelectItem value="departments">{L('Departamentos específicos', 'Specific departments')}</SelectItem>
                    <SelectItem value="users">{L('Colaboradores específicos', 'Specific collaborators')}</SelectItem>
                  </SelectContent>
                </Select>
                {draft.audience.type === 'departments' && (
                  <div className="mt-2 grid sm:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto border rounded-md p-2">
                    {depts.map((d) => (
                      <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={draft.audience.department_ids.includes(d.id)}
                                  onCheckedChange={(v) => setDraft({ ...draft, audience: { ...draft.audience,
                                    department_ids: v ? [...draft.audience.department_ids, d.id]
                                      : draft.audience.department_ids.filter((x) => x !== d.id) } })}
                                  data-testid={`comm-dept-${d.id}`} />
                        {d.name_es || d.name}
                      </label>
                    ))}
                  </div>
                )}
                {draft.audience.type === 'users' && (
                  <div className="mt-2 border rounded-md p-2">
                    <Input value={peopleQ} onChange={(e) => setPeopleQ(e.target.value)} className="h-8 mb-2"
                           placeholder={L('Buscar colaborador…', 'Search collaborator…')} data-testid="comm-people-search" />
                    <div className="grid sm:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                      {filteredPeople.map((u) => (
                        <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={draft.audience.user_ids.includes(u.id)}
                                    onCheckedChange={(v) => setDraft({ ...draft, audience: { ...draft.audience,
                                      user_ids: v ? [...draft.audience.user_ids, u.id]
                                        : draft.audience.user_ids.filter((x) => x !== u.id) } })}
                                    data-testid={`comm-user-${u.id}`} />
                          <span className="truncate">{u.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {draft.audience.user_ids.length} {L('seleccionados', 'selected')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>{L('Cancelar', 'Cancel')}</Button>
            <Button onClick={save} disabled={busy} data-testid="comm-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              {L('Guardar', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats dialog */}
      <Dialog open={!!attend} onOpenChange={(o) => !o && setAttend(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="comm-attendees-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{L('Asistentes', 'Attendees')} — {attend?.comm?.title}</DialogTitle>
          </DialogHeader>
          {attend && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <Badge className="bg-emerald-600 text-white border-0" data-testid="comm-attendees-count">
                  {attend.data.registered_count} {L('registrados', 'registered')}
                </Badge>
                {attend.data.capacity && <Badge variant="outline">{L('Cupo', 'Capacity')}: {attend.data.capacity}</Badge>}
                {attend.data.deadline && <Badge variant="outline">{L('Cierre', 'Deadline')}: {attend.data.deadline}</Badge>}
              </div>
              <div className="max-h-56 overflow-y-auto space-y-1.5">
                {(attend.data.items || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">{L('Nadie se ha registrado todavía.', 'No one has registered yet.')}</p>
                )}
                {(attend.data.items || []).map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded border px-2.5 py-1.5 text-sm">
                    <span>{a.user_name}</span>
                    <Badge variant="outline" className={`text-[10px] ${a.status === 'registered' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {a.status === 'registered' ? L('Registrado', 'Registered') : L('Cancelado', 'Cancelled')}
                    </Badge>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-xs font-medium">{L('Recordatorios (solo en la app — no se envían correos)', 'Reminders (in-app only — no emails sent)')}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={remBusy} onClick={() => sendReminder('pending')}
                          data-testid="comm-remind-pending">
                    {L('Recordar a pendientes', 'Remind pending')}
                  </Button>
                  <Button size="sm" variant="outline" disabled={remBusy} onClick={() => sendReminder('registered')}
                          data-testid="comm-remind-registered">
                    {L('Recordar a registrados', 'Remind registered')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!stats} onOpenChange={(o) => !o && setStats(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="comm-stats-dialog">
          <DialogHeader><DialogTitle>{L('Estadísticas', 'Statistics')} — {stats?.communication?.title}</DialogTitle></DialogHeader>
          {stats && (
            <>
              <div className="grid grid-cols-3 gap-3 text-center">
                {[[stats.totals.recipients, L('Destinatarios', 'Recipients')],
                  [stats.totals.viewed, L('Vistas', 'Viewed')],
                  [stats.totals.confirmed, L('Confirmadas', 'Confirmed')]].map(([v, l]) => (
                  <Card key={l} className="p-3">
                    <p className="text-2xl font-bold tabular-nums">{v}</p>
                    <p className="text-xs text-muted-foreground">{l}</p>
                  </Card>
                ))}
              </div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>{L('Colaborador', 'Collaborator')}</TableHead>
                  <TableHead><Eye className="h-3.5 w-3.5" /></TableHead>
                  <TableHead><CheckCircle2 className="h-3.5 w-3.5" /></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {stats.recipients.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm">{r.name}</TableCell>
                      <TableCell className="text-xs tabular-nums">{r.viewed_at ? r.viewed_at.slice(0, 16).replace('T', ' ') : '—'}</TableCell>
                      <TableCell className="text-xs tabular-nums">{r.confirmed_at ? r.confirmed_at.slice(0, 16).replace('T', ' ') : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
